import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, Clock, CheckCircle2, XCircle, AlertTriangle,
  ChevronLeft, Search, Eye, Loader2, X, Check, Ban,
  TrendingUp, Calendar, User, Info, Save, Plus, Trash2,
  Settings2, FileText, Wallet, ArrowDownUp, ArrowDownRight,
  ChevronRight, Edit2, RefreshCw, ClipboardCheck 
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// CONFIG
// ============================================================================
const STATUT_CONFIG = {
  en_attente: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  en_cours: { label: 'En cours', color: '#3b82f6', bg: '#dbeafe', icon: Loader2 },
  approuvee: { label: 'Approuvée', color: '#10b981', bg: '#d4f4dd', icon: CheckCircle2 },
  rejetee: { label: 'Rejetée', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
  annulee: { label: 'Annulée', color: '#6b7280', bg: '#f3f4f6', icon: Ban },
};

const TYPE_CONFIG = {
  depense: { label: 'Dépense', color: '#ef4444', icon: ArrowDownRight },
  transfert: { label: 'Transfert', color: '#3b82f6', icon: ArrowDownUp },
  commande: { label: 'Commande', color: '#f97316', icon: FileText },
};

const PRIORITE_CONFIG = {
  urgente: { label: 'Urgente', color: '#ef4444', bg: '#fee2e2' },
  haute: { label: 'Haute', color: '#f59e0b', bg: '#fef3c7' },
  normale: { label: 'Normale', color: '#6b7280', bg: '#f3f4f6' },
};

// ============================================================================
// MODULE APPROBATIONS
// ============================================================================
const ApprobationsModule = () => {
  const [demandes, setDemandes] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Vues
  const [currentView, setCurrentView] = useState('list'); // list | detail | config
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');

  // Config
  const [configs, setConfigs] = useState([]);
  const [approbateurs, setApprobateurs] = useState([]);

  // Modal décision
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decisionType, setDecisionType] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => { loadDemandes(); }, []);
  useEffect(() => { loadDemandes(); }, [filterStatut, filterType]);
  useEffect(() => { const t = setTimeout(() => loadDemandes(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadDemandes = async () => {
    showLoader(); setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatut) params.append('statut', filterStatut);
      if (filterType) params.append('type', filterType);
      if (searchTerm) params.append('search', searchTerm);
      const r = await api.approbations.lister(params.toString());
      if (r.success) {
        setDemandes(r.data || []);
        setStats(r.stats || null);
      }
    } catch (e) { console.error('Erreur chargement demandes:', e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadDetail = async (demande) => {
    setCurrentView('detail'); setLoadingDetail(true);
    try {
      const r = await api.approbations.getDetail(demande.id);
      if (r.success) setSelectedDemande(r.data);
    } catch (e) { showToast('Erreur chargement détail', 'error'); }
    finally { setLoadingDetail(false); }
  };

  const loadConfig = async () => {
    try {
      const [rc, ra] = await Promise.all([
        api.approbations.getConfig(),
        api.approbations.getApprobateurs()
      ]);
      if (rc.success) setConfigs(rc.data || []);
      if (ra.success) setApprobateurs(ra.data || []);
    } catch (e) { console.error(e); }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================
  const handleDecision = async () => {
    if (!selectedDemande) return;
    setIsSaving(true);
    try {
      const r = await api.approbations.decision(selectedDemande.id, { decision: decisionType, commentaire });
      if (r.success) {
        showToast(r.message || `Demande ${decisionType === 'approuve' ? 'approuvée' : 'rejetée'}`, 'success');
        setShowDecisionModal(false); setCommentaire('');
        await loadDemandes(); loadDetail({ id: selectedDemande.id });
      } else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur lors de la décision', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleAnnuler = async (id) => {
    try {
      const r = await api.approbations.annuler(id);
      if (r.success) { showToast('Demande annulée', 'success'); await loadDemandes(); if (currentView === 'detail') { setCurrentView('list'); setSelectedDemande(null); } }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur annulation', 'error'); }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  const getStatut = (s) => STATUT_CONFIG[s] || { label: s, color: '#6b7280', bg: '#f3f4f6', icon: Info };
  const getType = (t) => TYPE_CONFIG[t] || { label: t, color: '#6b7280', icon: Info };
  const getPrio = (p) => PRIORITE_CONFIG[p] || PRIORITE_CONFIG.normale;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Clock size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">En attente</span></div>
          <div className="text-2xl font-bold text-amber-600">{stats?.en_attente || 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">demandes</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #3b82f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Wallet size={18} className="text-blue-600" /><span className="text-xs font-semibold text-gray-500">Montant en attente</span></div>
          <div className="text-lg font-bold text-blue-600">{formatMontant(stats?.montant_en_attente || 0)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Approuvées aujourd'hui</span></div>
          <div className="text-2xl font-bold text-green-600">{stats?.approuvees_aujourd_hui || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><XCircle size={18} className="text-red-600" /><span className="text-xs font-semibold text-gray-500">Rejetées</span></div>
          <div className="text-2xl font-bold text-red-600">{stats?.rejetees || 0}</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher une demande..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Tous les types</option>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={() => { setCurrentView('config'); loadConfig(); }}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <Settings2 size={13} /> Configurer
            </button>
          </div>
        </div>
      </div>

      {/* Liste */}
      {isLoading ? null : demandes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <ShieldCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune demande</h3>
          <p className="text-sm text-gray-500">Les demandes d'approbation apparaîtront ici</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="divide-y divide-gray-100">
            {demandes.map(d => {
              const tc = getType(d.type_document);
              const sc = getStatut(d.statut);
              const pc = getPrio(d.priorite);
              const TypeIcon = tc.icon;
              const StatutIcon = sc.icon;
              const isActive = d.statut === 'en_attente' || d.statut === 'en_cours';

              return (
                <div key={d.id} onClick={() => loadDetail(d)} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    {/* Icône type */}
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: tc.color + '12' }}>
                      <TypeIcon size={20} style={{ color: tc.color }} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-gray-900 truncate">
                          {d.description || `${tc.label} #${d.document_id}`}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: sc.bg, color: sc.color }}>
                          {sc.label}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: pc.bg, color: pc.color }}>
                          {pc.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                        <span className="flex items-center gap-1"><User size={10} />{d.demandeur_nom || '-'}</span>
                        <span>Niveau {d.niveau_actuel}/{d.niveau_requis}</span>
                        <span>{formatDate(d.date_demande)}</span>
                      </div>
                    </div>

                    {/* Montant */}
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(d.montant)}</div>
                    </div>

                    {/* Actions rapides */}
                    {isActive && (
                      <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); loadDetail(d).then(() => { setDecisionType('approuve'); setShowDecisionModal(true); }); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50 hover:bg-green-100" title="Approuver">
                          <Check size={14} className="text-green-600" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); loadDetail(d).then(() => { setDecisionType('rejete'); setShowDecisionModal(true); }); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50 hover:bg-red-100" title="Rejeter">
                          <X size={14} className="text-red-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (loadingDetail) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>;
    if (!selectedDemande) return null;
    const d = selectedDemande;
    const tc = getType(d.type_document);
    const sc = getStatut(d.statut);
    const pc = getPrio(d.priorite);
    const TypeIcon = tc.icon;
    const isActive = d.statut === 'en_attente' || d.statut === 'en_cours';

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedDemande(null); }}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 mb-5"><ChevronLeft size={18} /> Retour à la liste</button>

        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${tc.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{d.description || `${tc.label} #${d.document_id}`}</h2>
                <span className="text-xs px-2 py-1 rounded-lg font-bold" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: pc.bg, color: pc.color }}>{pc.label}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>Demande #{d.id}</span>
                <span className="flex items-center gap-1"><User size={13} />{d.demandeur_nom || '-'}</span>
                <span className="flex items-center gap-1"><Calendar size={13} />{formatDate(d.date_demande)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {isActive && (
                <>
                  <button onClick={() => handleAnnuler(d.id)} className="px-4 py-2 rounded-xl text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1"><Ban size={14} /> Annuler</button>
                  <button onClick={() => { setDecisionType('rejete'); setShowDecisionModal(true); }} className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"><XCircle size={14} /> Rejeter</button>
                  <button onClick={() => { setDecisionType('approuve'); setShowDecisionModal(true); }} className="px-4 py-2 rounded-xl text-xs font-semibold text-white hover:opacity-90 flex items-center gap-1 shadow-md" style={{ backgroundColor: '#10b981' }}><CheckCircle2 size={14} /> Approuver</button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #1a1a2e', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Montant</div>
            <div className="text-xl font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(d.montant)}</div>
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: `3px solid ${tc.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Type</div>
            <div className="text-xl font-bold" style={{ color: tc.color }}>{tc.label}</div>
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #3b82f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Niveau</div>
            <div className="text-xl font-bold text-blue-600">{d.niveau_actuel} / {d.niveau_requis}</div>
          </div>
          {d.date_resolution && (
            <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div className="text-xs font-semibold text-gray-500 mb-1">Résolution</div>
              <div className="text-sm font-bold text-green-600">{formatDate(d.date_resolution)}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timeline décisions */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="px-6 py-4 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-800">Historique des décisions</h3></div>
            {(!d.decisions || d.decisions.length === 0) ? (
              <div className="p-12 text-center text-gray-400"><Clock className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">Aucune décision prise</p></div>
            ) : (
              <div className="divide-y divide-gray-50">
                {d.decisions.map((dec, i) => {
                  const isOk = dec.decision === 'approuve';
                  return (
                    <div key={i} className="px-6 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: isOk ? '#d4f4dd' : '#fee2e2' }}>
                          {isOk ? <Check size={16} className="text-green-600" /> : <X size={16} className="text-red-600" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold" style={{ color: isOk ? '#10b981' : '#ef4444' }}>
                              {isOk ? 'Approuvé' : 'Rejeté'} — Niveau {dec.niveau}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatDate(dec.date_decision)} — Par {dec.decideur_nom || 'Utilisateur'}
                          </div>
                          {dec.commentaire && (
                            <div className="text-xs text-gray-500 mt-1 pl-3 border-l-2 italic" style={{ borderColor: isOk ? '#10b981' : '#ef4444' }}>
                              {dec.commentaire}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Circuit de validation */}
            {d.niveaux_config && d.niveaux_config.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><TrendingUp size={16} style={{ color: '#448696' }} />Circuit de validation</h3>
                <div className="space-y-3">
                  {d.niveaux_config.map((niv) => {
                    const isDone = niv.niveau <= d.niveau_actuel;
                    const isCurrent = niv.niveau === d.niveau_actuel + 1;
                    return (
                      <div key={niv.niveau} className="flex items-center gap-3 p-3 rounded-xl"
                        style={{ backgroundColor: isDone ? '#d4f4dd' : isCurrent ? '#dbeafe' : '#f9fafb', border: isCurrent ? '1.5px solid #3b82f640' : '1px solid transparent' }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: isDone ? '#10b981' : isCurrent ? '#3b82f6' : '#d1d5db' }}>
                          {isDone ? <Check size={12} /> : niv.niveau}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-bold" style={{ color: isDone ? '#15803d' : isCurrent ? '#1d4ed8' : '#6b7280' }}>
                            {niv.label || `Niveau ${niv.niveau}`}
                          </div>
                          <div className="text-[10px] text-gray-400">{niv.approbateur_nom || niv.role_requis || 'Tout approbateur'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Document source */}
            {d.document_info && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FileText size={16} style={{ color: '#448696' }} />Document source</h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(d.document_info).map(([key, val]) => {
                    if (['id', 'company_id', 'created_at', 'updated_at'].includes(key) || val === null || val === undefined) return null;
                    return (
                      <div key={key} className="flex justify-between">
                        <span className="text-xs font-semibold text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-bold text-gray-700 text-right max-w-[180px] truncate">{String(val)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ============================================================================
  // VUE CONFIG
  // ============================================================================
  const renderConfig = () => (
    <ConfigView
      configs={configs}
      approbateurs={approbateurs}
      onBack={() => { setCurrentView('list'); loadDemandes(); }}
      onSave={loadConfig}
      showToast={showToast}
      formatMontant={formatMontant}
      inputStyle={inputStyle}
    />
  );

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' && renderList()}
      {currentView === 'detail' && renderDetail()}
      {currentView === 'config' && renderConfig()}

      {/* MODAL DÉCISION */}
      {showDecisionModal && selectedDemande && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 rounded-t-2xl flex items-center gap-3"
              style={{ backgroundColor: decisionType === 'approuve' ? '#10b981' : '#ef4444' }}>
              {decisionType === 'approuve' ? <CheckCircle2 size={22} className="text-white" /> : <XCircle size={22} className="text-white" />}
              <h3 className="text-lg font-bold text-white">
                {decisionType === 'approuve' ? 'Approuver la demande' : 'Rejeter la demande'}
              </h3>
            </div>
            <div className="p-6">
              <div className="mb-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{getType(selectedDemande.type_document).label}</span>
                  <span className="font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(selectedDemande.montant)}</span>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Commentaire {decisionType === 'rejete' && <span className="text-red-500">*</span>}
                </label>
                <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
                  placeholder={decisionType === 'approuve' ? 'Commentaire optionnel...' : 'Motif du rejet...'}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none" rows={4} {...inputStyle} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowDecisionModal(false); setCommentaire(''); }}
                  className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleDecision} disabled={isSaving || (decisionType === 'rejete' && !commentaire.trim())}
                  className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: decisionType === 'approuve' ? '#10b981' : '#ef4444' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (decisionType === 'approuve' ? <Check size={16} /> : <X size={16} />)}
                  {decisionType === 'approuve' ? 'Approuver' : 'Rejeter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

// ============================================================================
// VUE CONFIGURATION — Seuils et niveaux
// ============================================================================
const ConfigView = ({ configs, approbateurs, onBack, onSave, showToast, formatMontant, inputStyle }) => {
  const types = ['depense', 'transfert', 'commande'];
  const typeLabels = { depense: 'Dépenses', transfert: 'Transferts', commande: 'Commandes' };
  const typeColors = { depense: '#ef4444', transfert: '#3b82f6', commande: '#f97316' };
  const typeIcons = { depense: ArrowDownRight, transfert: ArrowDownUp, commande: FileText };

  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ actif: false, seuil_minimum: 0, niveaux: [] });
  const [saving, setSaving] = useState(false);

  const openConfig = (type) => {
    const existing = configs.find(c => c.type_document === type);
    if (existing) {
      setFormData({ actif: !!existing.actif, seuil_minimum: existing.seuil_minimum || 0, niveaux: existing.niveaux || [] });
    } else {
      setFormData({ actif: false, seuil_minimum: 0, niveaux: [] });
    }
    setEditingType(type);
  };

  const addNiveau = () => {
    const n = formData.niveaux.length + 1;
    setFormData(p => ({ ...p, niveaux: [...p.niveaux, { niveau: n, label: `Niveau ${n}`, seuil_max: null, approbateur_id: null, role_requis: '' }] }));
  };
  const removeNiveau = (idx) => {
    setFormData(p => ({ ...p, niveaux: p.niveaux.filter((_, i) => i !== idx).map((n, i) => ({ ...n, niveau: i + 1 })) }));
  };
  const updateNiveau = (idx, field, value) => {
    setFormData(p => ({ ...p, niveaux: p.niveaux.map((n, i) => i === idx ? { ...n, [field]: value } : n) }));
  };

  const handleSave = async () => {
    if (!editingType) return;
    setSaving(true);
    try {
      const r = await api.approbations.sauvegarderConfig(editingType, formData);
      if (r.success) { showToast('Configuration sauvegardée', 'success'); onSave(); setEditingType(null); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur sauvegarde', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 mb-5"><ChevronLeft size={18} /> Retour</button>

      <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: '4px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>Configuration des approbations</h2>
        <p className="text-sm text-gray-500 mt-1">Définissez les seuils et niveaux de validation par type d'opération</p>
      </div>

      {!editingType ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {types.map(type => {
            const existing = configs.find(c => c.type_document === type);
            const Icon = typeIcons[type]; const color = typeColors[type];
            return (
              <div key={type} onClick={() => openConfig(type)} className="bg-white rounded-2xl border border-gray-100 p-6 cursor-pointer transition-all hover:shadow-md" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + '12' }}><Icon size={22} style={{ color }} /></div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{typeLabels[type]}</h3>
                    <p className="text-xs text-gray-400">{existing ? (existing.actif ? 'Actif' : 'Désactivé') : 'Non configuré'}</p>
                  </div>
                </div>
                {existing && existing.actif ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Seuil minimum</span><span className="font-bold text-gray-800">{formatMontant(existing.seuil_minimum || 0)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Niveaux</span><span className="font-bold text-gray-800">{existing.nb_niveaux || 0}</span></div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Cliquez pour configurer</p>
                )}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-bold" style={{ color }}>Configurer</span>
                  <ChevronRight size={14} style={{ color }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {React.createElement(typeIcons[editingType], { size: 22, style: { color: typeColors[editingType] } })}
              <h3 className="text-lg font-bold text-gray-900">{typeLabels[editingType]}</h3>
            </div>
            <button onClick={() => setEditingType(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-400" /></button>
          </div>

          {/* Actif + Seuil */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Statut</label>
              <button onClick={() => setFormData(p => ({ ...p, actif: !p.actif }))}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 transition-all"
                style={{ borderColor: formData.actif ? '#10b981' : '#e5e7eb', backgroundColor: formData.actif ? '#f0fdf4' : '#fff' }}>
                <div className="w-10 h-6 rounded-full relative transition-all" style={{ backgroundColor: formData.actif ? '#10b981' : '#d1d5db' }}>
                  <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all" style={{ left: formData.actif ? '18px' : '2px' }} />
                </div>
                <span className="text-sm font-bold" style={{ color: formData.actif ? '#15803d' : '#6b7280' }}>{formData.actif ? 'Activé' : 'Désactivé'}</span>
              </button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Seuil minimum (FCFA)</label>
              <input type="number" value={formData.seuil_minimum} onChange={(e) => setFormData(p => ({ ...p, seuil_minimum: parseFloat(e.target.value) || 0 }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none" placeholder="100000" {...inputStyle} />
              <p className="text-xs text-gray-400 mt-1">En dessous de ce montant, validation automatique</p>
            </div>
          </div>

          {/* Niveaux */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-700">Niveaux d'approbation</h4>
              <button onClick={addNiveau} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#10b981' }}><Plus size={14} className="inline mr-1" />Ajouter</button>
            </div>
            {formData.niveaux.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                <TrendingUp size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-400">Ajoutez au moins un niveau</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.niveaux.map((niv, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-gray-200 bg-gray-50/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: typeColors[editingType] }}>{niv.niveau}</div>
                        <span className="text-sm font-bold text-gray-700">Niveau {niv.niveau}</span>
                      </div>
                      <button onClick={() => removeNiveau(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input type="text" value={niv.label} onChange={(e) => updateNiveau(idx, 'label', e.target.value)} placeholder="Label (ex: Chef service)"
                        className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" {...inputStyle} />
                      <input type="number" value={niv.seuil_max || ''} onChange={(e) => updateNiveau(idx, 'seuil_max', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Seuil max (vide = illimité)"
                        className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" {...inputStyle} />
                      <select value={niv.approbateur_id || ''} onChange={(e) => updateNiveau(idx, 'approbateur_id', e.target.value ? parseInt(e.target.value) : null)}
                        className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                        <option value="">Tout approbateur</option>
                        {approbateurs.map(a => <option key={a.id} value={a.id}>{a.username} ({a.role})</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button onClick={() => setEditingType(null)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: '#10b981' }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />} Sauvegarder
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprobationsModule;